import { ethers } from 'ethers';
import { ParseConfig, ParseResult, parse } from 'papaparse';

type ColumnType = 'string' | 'number' | 'address' | 'date';

interface ValidationOptions {
  headers: string[];
  columnTypes: ColumnType[];
  dateFormat?: string;
  encoding?: string;
}

interface CsvValidationResult {
  isValid: boolean;
  errors: string[];
  data?: any[];
}

export const parseConfig: ParseConfig = {
  header: false,
  skipEmptyLines: true,
  delimitersToGuess: [',', '\t', '|', ';'],
  transformHeader: (header: string) => header.trim(),
  transform: (value: string) => value.trim(),
};

export const validateCsv = (csvContent: string, options: ValidationOptions): CsvValidationResult => {
  const errors: string[] = [];
  const { headers: expectedHeaders, columnTypes } = options;

  if (expectedHeaders.length !== columnTypes.length) {
    throw new Error('Headers and columnTypes length mismatch.');
  }

  // Parse CSV
  const parseResult: ParseResult<any> = parse(csvContent, parseConfig);

  if (parseResult.errors.length > 0) {
    const parseErrorMessages = parseResult.errors.map(err => `Line ${(err.row || 0) + 1}: ${err.message}`);
    errors.push(...parseErrorMessages);
    return { isValid: false, errors };
  }

  const data = parseResult.data.filter(row => row.some((cell: string) => cell !== '')); // Remove empty rows

  // Validate headers
  const actualHeaders = data[0]?.map((header: string) => header.trim());
  if (!actualHeaders || !areHeadersEqual(actualHeaders, expectedHeaders)) {
    errors.push(`Invalid headers. Expected: ${expectedHeaders.join(',')}, Got: ${actualHeaders?.join(',')}`);
    return { isValid: false, errors };
  }

  // Helper functions with more robust validation
  const isValidAddress = (address: string): boolean => {
    try {
      return ethers.isAddress(address.trim());
    } catch {
      return false;
    }
  };

  const isValidNumber = (value: string): boolean => {
    const trimmedValue = value.trim().replace(',', '.');
    return !isNaN(Number(trimmedValue)) && Number(trimmedValue) >= 0;
  };

  const isValidDate = (date: string): boolean => {
    if (!date) return false;
    const trimmedDate = date.trim();

    // Support multiple date formats
    const datePatterns = [
      new RegExp('^\\d{4}-\\d{2}-\\d{2} \\d{2}:\\d{2}:\\d{2}$'), // YYYY-MM-DD HH:mm:ss
      new RegExp('^\\d{2}/\\d{2}/\\d{4} \\d{2}:\\d{2}:\\d{2}$'), // DD/MM/YYYY HH:mm:ss
      new RegExp('^\\d{4}-\\d{2}-\\d{2}$'), // YYYY-MM-DD
      new RegExp('^\\d{2}/\\d{2}/\\d{4}$'), // DD/MM/YYYY
    ];

    return datePatterns.some(pattern => pattern.test(trimmedDate));
  };

  // Validate rows
  for (let i = 1; i < data.length; i++) {
    const row = data[i];

    if (row.length !== columnTypes.length) {
      errors.push(`Row ${i + 1}: Expected ${columnTypes.length} columns, got ${row.length}.`);
      continue;
    }

    row.forEach((value: any, columnIndex: number) => {
      const columnType = columnTypes[columnIndex];
      const columnName = expectedHeaders[columnIndex];
      const trimmedValue = String(value).trim();

      if (!trimmedValue && columnType !== 'string') {
        errors.push(`Row ${i + 1}, Column "${columnName}": Empty value not allowed for type ${columnType}.`);
        return;
      }

      switch (columnType) {
        case 'string':
          // Allow empty strings but validate non-empty ones
          if (trimmedValue && typeof trimmedValue !== 'string') {
            errors.push(`Row ${i + 1}, Column "${columnName}": Expected a string.`);
          }
          break;

        case 'number':
          if (!isValidNumber(trimmedValue)) {
            errors.push(`Row ${i + 1}, Column "${columnName}": Expected a valid number, got "${trimmedValue}".`);
          }
          break;

        case 'address':
          if (!isValidAddress(trimmedValue)) {
            errors.push(
              `Row ${i + 1}, Column "${columnName}": Expected a valid Ethereum address, got "${trimmedValue}".`,
            );
          }
          break;

        case 'date':
          if (!isValidDate(trimmedValue)) {
            errors.push(`Row ${i + 1}, Column "${columnName}": Expected a valid date, got "${trimmedValue}".`);
          }
          break;

        default:
          errors.push(`Row ${i + 1}, Column "${columnName}": Unknown column type "${columnType}".`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    data: errors.length === 0 ? data.slice(1) : undefined, // Return parsed data if validation succeeds
  };
};

// Helper function to compare headers while ignoring case and whitespace
function areHeadersEqual(actual: string[], expected: string[]): boolean {
  if (actual.length !== expected.length) return false;

  return actual.every((header, index) => header.toLowerCase().trim() === expected[index].toLowerCase().trim());
}
